import { Link } from "react-router-dom";
import Seo from "../lib/seo";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { CONTACT } from "../lib/constants";

/**
 * Terms of Use — standard nonprofit advocacy website template.
 * This copy has NOT been reviewed by an attorney and does not constitute legal advice.
 */
export default function TermsOfUsePage() {
    const effectiveDate = "January 1, 2026";

    return (
        <>
            <Seo
                title="Terms of Use"
                description="Review the Terms of Use for the JC Law Foundation website, including information about website access, content, donations, and nonprofit advocacy resources."
                path="/terms-of-use"
            />

            <PageHeader
                overline="Legal"
                title="Terms of Use"
                subtitle={`Effective Date: ${effectiveDate}. Please read these Terms carefully before using the JC Law Foundation website.`}
            />

            <section className="bg-editorial py-20 md:py-28" data-testid="terms-of-use-content">
                <div className="max-w-[860px] mx-auto px-6 lg:px-10">
                    <Reveal>
                        <div className="bg-editorial-muted border border-editorial-border p-6 md:p-8 mb-10 text-sm text-navy-800/80 leading-relaxed">
                            <p><strong>Notice:</strong> These Terms of Use are provided for informational purposes only. They have not been reviewed by an attorney and do not constitute legal advice. Please consult qualified counsel before relying on this language.</p>
                        </div>
                    </Reveal>

                    <div className="space-y-8 text-navy-900">
                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">1. Acceptance of Terms</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                By accessing or using <a href="https://www.jclawsc.org" className="text-coral hover:underline">jclawsc.org</a> (the “Site”), you agree to these Terms of Use and our{" "}
                                <Link to="/privacy-policy" className="text-coral hover:underline">Privacy Policy</Link>. If you do not agree, please do not use the Site.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">2. About JC Law Foundation</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                JC Law Foundation (“JC Law,” “we,” “us,” or “our”) is a registered 501(c)(3) nonprofit organization advocating for CPR training, AED access, and stronger cardiac emergency preparedness in hotels.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">3. Informational Purposes Only</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                Content on the Site is provided for general awareness, education, and advocacy purposes. It is not medical, legal, or professional advice. Do not rely on Site content in place of consultation with a qualified professional. In an emergency, call 911 immediately.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">4. Use of the Site</h2>
                            <p className="text-navy-800/85 leading-relaxed mb-3">You agree to use the Site only for lawful purposes. You will not:</p>
                            <ul className="list-disc pl-6 space-y-2 text-navy-800/85 leading-relaxed">
                                <li>Attempt to interfere with or disrupt the Site or its servers.</li>
                                <li>Attempt to gain unauthorized access to any portion of the Site.</li>
                                <li>Use the Site to transmit unsolicited communications, harassing content, or malicious code.</li>
                                <li>Impersonate any person or misrepresent your affiliation with any person or entity.</li>
                                <li>Copy, reproduce, or redistribute Site content without prior written permission, except as expressly permitted by these Terms.</li>
                            </ul>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">5. Intellectual Property</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                All text, graphics, logos, images, and other materials on the Site are the property of JC Law Foundation or its licensors and are protected by applicable intellectual property laws. You may share links to the Site and quote short portions with attribution for educational or news reporting purposes. Any other use requires prior written permission.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">6. Donations</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                Donations to JC Law Foundation are processed by our third-party payment partner (Givebutter) and are subject to Givebutter’s terms of service. Donations are generally not refundable except at our sole discretion or as required by law. Contributions may be tax-deductible to the extent permitted by law; please consult a tax professional.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">7. Third-Party Links and Services</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                The Site may contain links to third-party websites, including Change.org, Givebutter, and social media platforms. JC Law Foundation does not control and is not responsible for the content, policies, or practices of any third-party site or service.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">8. Petition Progress Information</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                Any petition progress figures displayed on the Site are provided for informational purposes and reflect information available from Change.org at the time of access. Actual current totals are maintained by Change.org.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">9. Disclaimers</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                THE SITE AND ITS CONTENT ARE PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">10. Limitation of Liability</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                To the fullest extent permitted by law, JC Law Foundation and its directors, officers, volunteers, and agents will not be liable for any indirect, incidental, consequential, special, or punitive damages arising out of or related to your use of the Site.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">11. Indemnification</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                You agree to indemnify and hold harmless JC Law Foundation from any claim, demand, or expense (including reasonable attorneys’ fees) arising out of your violation of these Terms or misuse of the Site.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">12. Governing Law</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                These Terms are governed by the laws of the State of South Carolina, without regard to conflict-of-law principles. Any disputes arising out of or related to these Terms or the Site shall be brought exclusively in the state or federal courts located in South Carolina.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">13. Changes to These Terms</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                We may update these Terms of Use at any time. The “Effective Date” above will reflect the latest revision. Continued use of the Site after changes constitutes acceptance of the revised Terms.
                            </p>
                        </Reveal>

                        <Reveal>
                            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">14. Contact Us</h2>
                            <p className="text-navy-800/85 leading-relaxed">
                                Questions about these Terms? Email{" "}
                                <a href={`mailto:${CONTACT.general}`} className="text-coral hover:underline">{CONTACT.general}</a>{" "}
                                or mail us at {CONTACT.location}.
                            </p>
                        </Reveal>

                        <Reveal>
                            <div className="pt-8 border-t border-editorial-border mt-10 text-sm text-navy-700/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <p>See also our <Link to="/privacy-policy" className="text-coral hover:underline">Privacy Policy</Link>.</p>
                                <p>© {new Date().getFullYear()} JC Law Foundation. Registered 501(c)(3) Nonprofit Organization.</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
}
